import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type SiteTheme = 'black-white' | 'deep-blue' | 'forest-green' | 'yelo' | 'terracotta' | 'deep-purple' | 'charcoal';

const ThemeStorageKey = 'site-theme';
const ThemeClassPrefix = 'theme-';
const AvailableThemes: SiteTheme[] = ['black-white', 'deep-blue', 'forest-green', 'yelo', 'terracotta', 'deep-purple', 'charcoal'];

@Injectable({
  providedIn: 'root'
})
export class SiteThemeService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  readonly currentTheme = signal<SiteTheme>('black-white');

  constructor() {
    this.applyTheme(this.getSavedTheme(), false);
  }

  setTheme(theme: SiteTheme): void {
    this.applyTheme(theme, true);
  }

  toggleTheme(): void {
    const currentIndex = AvailableThemes.indexOf(this.currentTheme());
    const nextTheme = AvailableThemes[(currentIndex + 1) % AvailableThemes.length];
    this.setTheme(nextTheme);
  }

  private applyTheme(theme: SiteTheme, persist: boolean): void {
    if (!this.isBrowser) {
      this.currentTheme.set(theme);
      return;
    }

    const body = document.body;
    for (const availableTheme of AvailableThemes) {
      body.classList.remove(`${ThemeClassPrefix}${availableTheme}`);
    }

    body.classList.add(`${ThemeClassPrefix}${theme}`);
    this.currentTheme.set(theme);

    if (persist) {
      localStorage.setItem(ThemeStorageKey, theme);
    }
  }

  private getSavedTheme(): SiteTheme {
    if (!this.isBrowser) {
      return 'black-white';
    }

    const savedTheme = localStorage.getItem(ThemeStorageKey) as SiteTheme | null;
    return savedTheme && AvailableThemes.includes(savedTheme) ? savedTheme : 'black-white';
  }
}
